/**
 * AutomationsSection — Hive Flows pinned to the project.
 *
 * Phase 3 of the "Project as ecosystem of work" track (June 9, 2026).
 * Backed by ``GET /projects/{id}/automations`` which serves a compact
 * projection of the user's flows scoped to a single project. Tapping
 * a row deep-links to the workspace ``AutomationsHub`` filtered on the
 * flow so the user lands directly on its editor. The "+ Nuovo" CTA
 * jumps to the new-flow flow with ``?project_id=<this>`` pre-applied
 * (the Hub already supports the search param — Phase 3.5 will wire the
 * pre-apply explicitly; for now the link is the discoverability hook).
 *
 * No new picker / editor is introduced here: the project surface stays
 * thin and reuses the existing flow editor exactly as the Hub does.
 * Adding inline edit would duplicate ``FlowCanvas`` + ``NodeInspector``
 * + the whole approval machinery — not worth the scope creep.
 */

import { msg } from "@lingui/core/macro";
import type { I18n } from "@lingui/core";
import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import {
  Bell,
  Calendar,
  Pause,
  Play,
  Plus,
  Webhook,
  Workflow,
  Zap,
} from "lucide-react";
import type * as React from "react";

import type { ProjectAutomationSummary } from "@ando/core/api";
import { AlertBanner, Button, SurfaceEmptyState, SurfaceSkeleton, cn } from "@ando/ui";

import { useProjectAutomations } from "../../hooks/useProjects.js";
import { formatRelativeDateI18n } from "../../utils/relativeDate.js";

export interface AutomationsSectionProps {
  readonly projectId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function pickScheduleIcon(scheduleType: string): React.ComponentType<{
  className?: string;
}> {
  if (scheduleType === "cron" || scheduleType === "interval" || scheduleType === "once") {
    return Calendar;
  }
  if (scheduleType === "webhook") return Webhook;
  if (scheduleType === "reactive") return Zap;
  return Workflow;
}

function describeSchedule(
  i18n: I18n,
  scheduleType: string,
  cronExpression: string | null,
  intervalMinutes: number | null,
  nextRunAt: string | null,
): string {
  if (scheduleType === "cron" && cronExpression) {
    return `cron · ${cronExpression}`;
  }
  if (scheduleType === "interval" && intervalMinutes) {
    if (intervalMinutes % 60 === 0) {
      const h = intervalMinutes / 60;
      return h === 1
        ? i18n._(msg`ogni ${h} ora`)
        : i18n._(msg`ogni ${h} ore`);
    }
    return i18n._(msg`ogni ${intervalMinutes} min`);
  }
  if (scheduleType === "webhook") return i18n._(msg`trigger via webhook`);
  if (scheduleType === "reactive") return i18n._(msg`trigger reattivo`);
  if (scheduleType === "once" && nextRunAt) {
    const t = Date.parse(nextRunAt);
    if (!Number.isNaN(t)) {
      const d = new Date(t);
      const dd = String(d.getDate()).padStart(2, "0");
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      const when = `${dd}/${mo} ${hh}:${mm}`;
      return i18n._(msg`una volta · ${when}`);
    }
  }
  return i18n._(msg`manuale`);
}

// Forward-facing (next run) with time of day. Lingui-aware so the
// anchor labels (``oggi`` / ``domani``) round-trip through the
// extracted catalog. Past-anchor (``ieri``) is suppressed because a
// future schedule can't land there.

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

const AutomationRow: React.FC<{ readonly flow: ProjectAutomationSummary }> = ({
  flow,
}) => {
  const { i18n } = useLingui();
  const ScheduleIcon = pickScheduleIcon(flow.schedule_type);
  const scheduleLabel = describeSchedule(
    i18n,
    flow.schedule_type,
    flow.cron_expression,
    flow.interval_minutes,
    flow.next_run_at,
  );
  const nextRunLabel = flow.next_run_at
    ? formatRelativeDateI18n(flow.next_run_at, i18n, {
        withTime: true,
        recogniseYesterday: false,
      })
    : "";
  const nextRun = nextRunLabel.length > 0 ? nextRunLabel : null;

  // Status: ``active`` + ``is_active`` = running, otherwise show the raw
  // status. Approval-pending flows surface a yellow chip via the BE
  // ``status`` field downstream — for the project pane we keep the
  // taxonomy simple (live / paused / draft / archived).
  const isLive = flow.is_active && flow.status === "active";

  return (
    <li>
      <Link
        to="/workspace/flows/$flowId"
        params={{ flowId: flow.flow_id }}
        aria-label={i18n._(msg`Apri automazione: ${flow.name}`)}
        className={cn(
          "group flex items-start gap-3 rounded-xl border border-hairline bg-white/70 p-3.5 shadow-sm",
          "transition-colors hover:bg-ink-1/[0.03]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
        )}
      >
        <span
          aria-hidden
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
            "bg-ando-orange-soft text-ando-orange",
          )}
        >
          <ScheduleIcon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className="truncate font-[550] text-[13px] text-ink-1 leading-tight"
              title={flow.name}
            >
              {flow.name}
            </span>
            <span
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 font-medium text-[10.5px]",
                isLive
                  ? "bg-success/15 text-success"
                  : flow.status === "paused"
                    ? "bg-warning/15 text-warning"
                    : "bg-ink-1/[0.05] text-ink-3",
              )}
            >
              {isLive ? (
                <Play className="h-2.5 w-2.5" aria-hidden />
              ) : flow.status === "paused" ? (
                <Pause className="h-2.5 w-2.5" aria-hidden />
              ) : null}
              {isLive ? (
                <Trans>attivo</Trans>
              ) : flow.status === "paused" ? (
                <Trans>in pausa</Trans>
              ) : flow.status === "draft" ? (
                <Trans>bozza</Trans>
              ) : (
                <span>{flow.status}</span>
              )}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-3 tabular-nums">
            <span className="non-tabular-nums">{scheduleLabel}</span>
            {nextRun ? (
              <>
                <span aria-hidden>·</span>
                <span>
                  <Trans>prossima {nextRun}</Trans>
                </span>
              </>
            ) : null}
            {flow.total_runs > 0 ? (
              <>
                <span aria-hidden>·</span>
                <span>
                  <Trans>{flow.total_runs} esecuzioni</Trans>
                </span>
              </>
            ) : null}
          </div>
        </div>
      </Link>
    </li>
  );
};

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export const AutomationsSection: React.FC<AutomationsSectionProps> = ({
  projectId,
}) => {
  const { i18n } = useLingui();
  const query = useProjectAutomations(projectId);

  if (query.isLoading) {
    return (
      <SurfaceSkeleton
        variant="card-row"
        count={3}
        ariaLabel={i18n._(msg`Caricamento automazioni`)}
      />
    );
  }

  if (query.isError) {
    return (
      <AlertBanner tone="error">
        <Trans>
          Impossibile caricare le automazioni. Riprova fra poco.
        </Trans>
      </AlertBanner>
    );
  }

  const flows = query.data ?? [];

  if (flows.length === 0) {
    return (
      <SurfaceEmptyState
        icon={<Bell className="h-5 w-5" />}
        title={<Trans>Nessuna automazione per questo progetto</Trans>}
        description={
          <Trans>
            Crea un flusso che si attiva su un calendario o un evento e fissalo
            al progetto: le esecuzioni resteranno scoped qui dentro.
          </Trans>
        }
        action={
          <Button variant="primary" size="sm" asChild>
            <Link to="/workspace/automations">
              <Plus className="h-3.5 w-3.5" aria-hidden />
              <Trans>Crea automazione</Trans>
            </Link>
          </Button>
        }
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[11.5px] text-ink-3">
          <Trans>
            <strong className="font-medium text-ink-2">{flows.length}</strong>{" "}
            automazioni del progetto.
          </Trans>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/workspace/automations">
            <Plus className="h-3.5 w-3.5" aria-hidden />
            <Trans>Nuova</Trans>
          </Link>
        </Button>
      </div>
      <ul className="flex flex-col gap-2">
        {flows.map((f) => (
          <AutomationRow key={f.flow_id} flow={f} />
        ))}
      </ul>
    </div>
  );
};

AutomationsSection.displayName = "AutomationsSection";
